import { useState } from 'react';

import AnalyticsEvents from '~/analytics';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useDatadog } from '~/lib/hooks/useDatadog';
import { useDeleteSMTEntryMutation } from '~/store/services/SMT';

interface UseDeleteSMTEntryParams {
  patientId: string;
}

interface UseDeleteSMTEntryReturn {
  entryIdToDelete: string | null;
  handleCloseDeleteModal: () => void;
  handleConfirmDelete: () => Promise<void>;
  handleDeleteEntry: (entryId: string) => void;
  isDeleteModalOpen: boolean;
  isLoading: boolean;
}

export const useDeleteSMTEntry = ({
  patientId,
}: UseDeleteSMTEntryParams): UseDeleteSMTEntryReturn => {
  const [entryIdToDelete, setEntryIdToDelete] = useState<string | null>(null);
  const { trackEvent } = useAnalytics();
  const { logError } = useDatadog();
  const [deleteSMTEntry, { isLoading }] = useDeleteSMTEntryMutation();

  const handleDeleteEntry = (entryId: string) => {
    setEntryIdToDelete(entryId);
    trackEvent(AnalyticsEvents.deleteEntryButtonClicked, {
      entryId,
      patientId,
      source: AnalyticsSourceValues.SMT_TAB,
    });
  };

  const handleConfirmDelete = async () => {
    if (!entryIdToDelete) return;

    trackEvent(AnalyticsEvents.deleteEntryConfirmed, {
      entryId: entryIdToDelete,
      patientId,
      source: AnalyticsSourceValues.SMT_TAB,
    });

    try {
      await deleteSMTEntry({
        patientId,
        smtEntryId: entryIdToDelete,
      }).unwrap();
      setEntryIdToDelete(null);
    } catch (error) {
      logError(error, {
        action: 'deleteSMTEntry',
        context: 'Failed to delete SMT entry',
        entryId: entryIdToDelete,
        patientId,
      });
    }
  };

  const handleCloseDeleteModal = () => {
    if (entryIdToDelete) {
      trackEvent(AnalyticsEvents.deleteEntryCanceled, {
        entryId: entryIdToDelete,
        patientId,
        source: AnalyticsSourceValues.SMT_TAB,
      });
    }
    setEntryIdToDelete(null);
  };

  return {
    entryIdToDelete,
    handleCloseDeleteModal,
    handleConfirmDelete,
    handleDeleteEntry,
    isDeleteModalOpen: entryIdToDelete !== null,
    isLoading,
  };
};
